import * as LabelPrimitive from "@radix-ui/react-label";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { cn } from "@/lib/utils";

const labelVariants = cva(
  "tw-text-sm tw-font-medium tw-leading-none peer-disabled:tw-cursor-not-allowed peer-disabled:tw-opacity-70"
);

const Label = React.forwardRef<
  React.ElementRef<typeof LabelPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof LabelPrimitive.Root> &
    VariantProps<typeof labelVariants>
>(({ className, children, ...props }, ref) => {
  // Handle string children ending with asterisk
  if (typeof children === "string") {
    const hasAsterisk = children.trim().endsWith("*");
    if (hasAsterisk) {
      const textWithoutAsterisk = children.replace(/\s?\*$/, "");
      return (
        <LabelPrimitive.Root
          ref={ref}
          className={cn(labelVariants(), className)}
          {...props}
        >
          {textWithoutAsterisk}
          <span className="tw-text-destructive"> *</span>
        </LabelPrimitive.Root>
      );
    }
  }

  // Handle JSX/mixed content - check if last text node ends with asterisk
  const processChildren = (child: React.ReactNode): React.ReactNode => {
    if (typeof child === "string") {
      const trimmed = child.trim();
      if (trimmed.endsWith("*")) {
        const textWithoutAsterisk = child.replace(/\s?\*$/, "");
        return (
          <>
            {textWithoutAsterisk}
            <span className="tw-text-destructive"> *</span>
          </>
        );
      }
    }
    return child;
  };

  // Process children array
  if (Array.isArray(children)) {
    const processed = children.map((child, index) => {
      // Only process the last child for asterisk
      // Using index as key is acceptable here since children order is stable and derived from props
      if (index === children.length - 1) {
        return (
          <React.Fragment key={`label-child-${index}`}>
            {processChildren(child)}
          </React.Fragment>
        );
      }
      return (
        <React.Fragment key={`label-child-${index}`}>{child}</React.Fragment>
      );
    });
    return (
      <LabelPrimitive.Root
        ref={ref}
        className={cn(labelVariants(), className)}
        {...props}
      >
        {processed}
      </LabelPrimitive.Root>
    );
  }

  return (
    <LabelPrimitive.Root
      ref={ref}
      className={cn(labelVariants(), className)}
      {...props}
    >
      {processChildren(children)}
    </LabelPrimitive.Root>
  );
});
Label.displayName = LabelPrimitive.Root.displayName;

export { Label };
